import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { LitElement, css, html, nothing, when } from '@lit'

const MENU_DELAY = 400

class DNADesktopNav extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    key: { type: Number },
    active: { type: Boolean, reflect: true },
    href: { type: String },
    navTitle: { type: String },
    hasMenu: { type: Boolean, attribute: 'has-menu' },
    hovered: { type: Boolean },
  }

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.hovered = false
  }

  connectedCallback() {
    super.connectedCallback()

    this.l1text = this.getAttribute('navTitle')
    document.addEventListener('dna:header:close', this._handleOverlayMouseover)
    this.addEventListener('focusin', this._handleItemFocus)
    this.addEventListener('focusin', this._handleLinkMouseover)
    this.addEventListener('mouseenter', this._handleLinkMouseover)
    this.addEventListener('click', this._menuItemClickHandler)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(
      'dna:header:close',
      this._handleOverlayMouseover,
    )
    this.removeEventListener('focusin', this._handleItemFocus)
    this.removeEventListener('focus', this._handleLinkMouseover)
    this.removeEventListener('mouseover', this._handleLinkMouseover)
    this.removeEventListener('click', this._menuItemClickHandler)
  }

  _updateChildrenAccess = (index = 1) => {
    // TODO: refactor this to avoid query selector (currently needed to find slotted a tags)
    const $childLinks = this.querySelectorAll('a')
    for (let i = 0; i < $childLinks.length; i++) {
      const $link = $childLinks[i]
      $link.setAttribute('tabindex', index)
    }
  }

  _handleItemFocus = (event) => {
    const item = event.target.closest('a')
    if (item && this.contains(event.target)) {
      this.active = true
    }
  }

  _handleLinkMouseover = (event) => {
    const $menuSection = event.target.closest('.js-main-menu-section')

    if ($menuSection) {
      // Dispatch an event that will close all other menus first
      document.dispatchEvent(new CustomEvent('dna:country-selector:hide'))
      document.dispatchEvent(new CustomEvent('dna:search-bar:close'))
      this.dispatchEvent(
        new CustomEvent('dna:header:close', { bubbles: true, composed: true }),
      )
      this.active = true
      this._updateChildrenAccess(0)

      if (this.hasMenu) {
        this.setAttribute('aria-expanded', true)
        if (!this.hovered) {
          this._trackMegaMenuOpen()
          this.hovered = true
        }
      }
    }
  }

  _handleOverlayMouseover = (event) => {
    this.active = false
    this.setAttribute('aria-expanded', false)
    if (
      document.activeElement.classList.contains('js-main-menu-section') ||
      this.contains(document.activeElement)
    ) {
      return
    }
    this._updateChildrenAccess(-1)

    if (
      event.type !== 'dna:header:close' &&
      event?.target?.classList.contains('overlay')
    ) {
      this.hovered = false
    }
  }

  _menuItemClickHandler(event) {
    const $clickedLink = event.target.closest('a')

    // L1
    if (event.target.closest('.js-main-menu-section-item')) {
      const $menuSection = event.target.closest('.js-main-menu-section')
      const $menuSectionLink =
        $menuSection.renderRoot.querySelector('.js-menu-item')
      if ($menuSectionLink) {
        this.l1link = $menuSectionLink.getAttribute('href')?.trim()
        this.l2link = false
        this.l2text = false
        this.l3link = false
        this.l3text = false
        this._trackMenuItemClick()
      }
    }
    // L2
    if (
      event.target.closest('.js-solo-menu-item') ||
      event.target.closest('.js-featured-menu-item')
    ) {
      this.l2link = $clickedLink?.getAttribute('href')?.trim()
      this.l2text = $clickedLink?.textContent?.trim()
      this.l3link = false
      this.l3text = false
      this._trackMenuItemClick()
    }
    // L2 & L3
    if (event.target.closest('.js-main-menu-child')) {
      const $menuChild = event.target.closest('.js-main-menu-child')
      this.l2text = $menuChild
        .querySelector('.js-main-menu-child-title')
        ?.textContent?.trim()

      if (event.target.closest('.js-main-menu-child-title')) {
        this.l2link = $clickedLink?.getAttribute('href')?.trim()
        this.l3link = false
        this.l3text = false
        this._trackMenuItemClick()
      }
      if (event.target.closest('.js-main-menu-child-item')) {
        this.l3link = $clickedLink?.getAttribute('href')?.trim()
        this.l3text = $clickedLink?.textContent?.trim()
        this._trackMenuItemClick()
      }
    } else {
      if (event.target.closest('.js-main-menu-child-item')) {
        this.l2link = $clickedLink?.getAttribute('href')?.trim()
        this.l2text = $clickedLink?.textContent?.trim()
        this.l3link = false
        this.l3text = false
        this._trackMenuItemClick()
      }
    }
  }

  _trackMegaMenuOpen() {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.MAIN_MENU_SECTION_EXPANDED, {
        main_menu_section: this.navTitle,
      })
    })
  }

  _trackMenuItemClick() {
    // @segment
    Segment.protect(() => {
      const menuItemLink =
        !this.l3link && !this.l2link
          ? this.l1link
          : !this.l3link
            ? this.l2link
            : this.l3link
      const menuItemText =
        !this.l3text && !this.l2text
          ? this.l1text
          : !this.l3text
            ? this.l2text
            : this.l3text
      let menuItemTreeString
      if (this.l1text) {
        menuItemTreeString = this.l1text
      }
      if (this.l2text) {
        if (this.l1text) {
          menuItemTreeString = `${this.l1text} - ${this.l2text}`
        } else {
          menuItemTreeString = this.l2text
        }
      }
      if (this.l3text) {
        if (this.l1text && this.l2text) {
          menuItemTreeString = `${this.l1text} - ${this.l2text} - ${this.l3text}`
        } else if (this.l2text) {
          menuItemTreeString = `${this.l2text} - ${this.l3text}`
        } else {
          menuItemTreeString = this.l3text
        }
      }

      Segment.track(Segment.CONSTANTS.EVENTS.MAIN_MENU_ITEM_CLICKED, {
        main_menu_item: menuItemTreeString,
        main_menu_item_link: menuItemLink,
        main_menu_item_text: menuItemText,
      })
    })
  }

  render() {
    return html`
      ${when(
        this.isMobile,
        () => nothing,
        () => html`
          <li>
            ${when(
              this.href === '#',
              () => html`
                <div tabindex="0">
                  <slot name="link"></slot>
                </div>
              `,
              () => html`
                <a href=${this.href} class="js-menu-item">
                  <slot name="link"></slot>
                </a>
              `,
            )}
            <div class="menu">
              <slot name="menu"></slot>
            </div>
            <div @mouseover=${this._handleOverlayMouseover} class="overlay"></div>
          </li>
        `,
      )}
    `
  }

  static styles = css`
    :host([active]) .overlay {
      visibility: visible;
      opacity: 1;
    }
    :host([active]) .menu {
      max-height: 1000px;
      transition: max-height 1s ease;
      transition-delay: ${MENU_DELAY}ms;
    }
    li > a {
      color: inherit;
      cursor: pointer;
      text-decoration: none;
    }
    li > div {
      cursor: default;
    }
    :host(:not([has-menu])) .menu {
      display: none;
    }
    :host(:not([has-menu])) .overlay {
      display: none;
    }
    .menu {
      position: absolute;
      width: 100%;
      left: 0;
      top: 100%;
      z-index: 1;
      overflow: hidden;
      height: auto;
      max-height: 0px;
    }
    .overlay {
      position: fixed;
      width: 100%;
      height: 100%;
      min-height: 100vh;
      top: var(--dna-header-height);
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(1, 0, 0, 0.5);
      z-index: -1;
      visibility: hidden;
      opacity: 0;
    }
  `
}

class DNADesktopNavChild extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    href: { type: String },
    current: { type: Boolean, reflect: true },
  }

  constructor() {
    super()
    this._setMobileBreakpoints(['mobile', 'tablet'])
  }

  render() {
    return html`
      ${when(
        this.isMobile,
        () => nothing,
        () => html`
          <div
            ?current=${this.current}
            class="wrapper"
          >
            ${when(
              this.href === null,
              () => html`
                <a
                  href=${this.href}
                  class="js-main-menu-child-title js-menu-item"
                >
                  <slot
                    name="title"
                    class="js-main-menu-title-slot"
                  ></slot>
                </a>
              `,
              () => html`
                <slot
                  name="title"
                  class="js-main-menu-title-slot js-menu-item"
                ></slot>
              `,
            )}
            <div class="inner-links">
              <slot></slot>
            </div>
          </div>
        `,
      )}
    `
  }

  static styles = css`
    .wrapper {
      page-break-inside: avoid;
      break-inside: avoid-column;
    }
    a {
      text-decoration: none;
      color: inherit;
    }

    .inner-links {
      margin-top: var(--dna-space-4);
      padding-left: var(--dna-space-4);
      border-left: .5px solid var(--dna-color-light-grey);
      margin-bottom: var(--dna-space-10);
    }

    ::slotted(.main-menu-child-item-list) {
      display: flex;
      flex-direction: column;
      gap: calc(var(--dna-space-5) / 2);
      margin-top: calc(var(--dna-space-2) * -1)

    }
  `
}

customElements.define('dna-desktop-nav', DNADesktopNav)
customElements.define('dna-desktop-nav-child', DNADesktopNavChild)
